import classNames from 'classnames'
import React, { useEffect, useRef, useState } from 'react'

export enum D_DAY_TYPE {
  DAY = 'day',
  HOUR = 'hour',
  DAY_TO_HOURS = 'day_to_hours',
  MIN = 'min',
  SEC = 'sec',
  MILLI_SEC = 'ms',
}

const MAX_TIMEOUT = 0x7fffffff

const getTimerProps: (type: D_DAY_TYPE) => { interval: number; modular: number; minInterval?: number } = (
  type: D_DAY_TYPE
) => {
  switch (type) {
    case D_DAY_TYPE.DAY:
      return { interval: 1000 * 60 * 60 * 24, modular: Infinity, minInterval: MAX_TIMEOUT }
    case D_DAY_TYPE.HOUR:
      return { interval: 1000 * 60 * 60, modular: 72, minInterval: MAX_TIMEOUT }
    case D_DAY_TYPE.DAY_TO_HOURS:
      return { interval: 1000 * 60 * 60, modular: 24, minInterval: MAX_TIMEOUT }
    case D_DAY_TYPE.MIN:
      return { interval: 1000 * 60, modular: 60, minInterval: MAX_TIMEOUT }
    case D_DAY_TYPE.SEC:
      return { interval: 1000, modular: 60, minInterval: MAX_TIMEOUT }
    case D_DAY_TYPE.MILLI_SEC:
      return { interval: 10, modular: 60, minInterval: MAX_TIMEOUT }
  }
}

interface PartialDdayCounterProps {
  initialDay: Date
  targetDay: Date // UTC 기준으로 넣어주어야 정상 동작
  type: D_DAY_TYPE
  padReplaceD?: boolean
  classNameDigitContainer?: string // 기본 스타일 없음 - 충돌 고려할 필요 X
  classNameDigit?: string // 기본 스타일 없음 - 충돌 고려할 필요 X
}
export const PartialDdayCounter = (props: PartialDdayCounterProps) => {
  /**
   * 날짜, 시간, 분, 초를 옵션으로 입력받아 두 자리로 나타내는 컴포넌트 반환
   * setTimeout() recursive 하게 사용해 구현
   * 처음에는 바로 다음 시간이 변경되는 시점에 타이머를 맞춰 두고,
   * 이후부터는 타이머가 콜 된 시점을 기준으로 다음 시간 변경되는 시점에 타이머 맞춤
   */
  const { initialDay, targetDay, type, classNameDigitContainer, classNameDigit, padReplaceD } = props
  const { interval, modular, minInterval } = getTimerProps(type)

  const [diffInMs, setDiffInMs] = useState<number>(0)

  const scheduledTime = useRef<number | null>(null)
  const timeout = useRef<NodeJS.Timeout | null>(null)

  const timer = () => {
    const now = Date.now()

    const delay = Math.max(now - scheduledTime.current, 0)
    const cntInterval = now < scheduledTime.current ? 0 : Math.floor(delay / interval) + 1

    if (cntInterval) {
      setDiffInMs((prev) => {
        if (prev < cntInterval * interval) {
          clearTimeout(timeout.current)
          return 0
        } else {
          return prev - cntInterval * interval
        }
      })

      scheduledTime.current += cntInterval * interval
    }

    // 얼마만큼의 시간 후에 타이머 wake-up 할지 계산
    const realInterval = scheduledTime.current - now

    timeout.current = setTimeout(timer, Math.min(realInterval, minInterval))
  }

  const onVisible = (e: Event) => {
    if (diffInMs >= interval) {
      const visiblilityState = (e.target as Document).visibilityState
      if (visiblilityState === 'visible') {
        clearTimeout(timeout.current)
        timer()
      }
    }
  }

  useEffect(() => {
    setDiffInMs(Math.max(targetDay.getTime() - initialDay.getTime(), 0)) // ????
    addEventListener('visibilitychange', onVisible)
    if (targetDay.getTime() < initialDay.getTime() + interval) return

    scheduledTime.current = initialDay.getTime() + ((targetDay.getTime() - initialDay.getTime()) % interval) // 바로 다음 타이머 예정 시간
    timeout.current = setTimeout(timer, Math.min(scheduledTime.current - Date.now(), minInterval))

    return () => {
      clearTimeout(timeout.current)
      removeEventListener('visibilitychange', onVisible)
    }
  }, [targetDay, interval])

  let timeString = String(Math.floor(diffInMs / interval) % modular).padStart(2, '0')

  const isDayToHours = type === D_DAY_TYPE.DAY_TO_HOURS
  if (isDayToHours) {
    const totalHours = Math.floor(diffInMs / (1000 * 60 * 60)) % 24
    timeString = String(totalHours).padStart(2, '0')
  }

  return (
    <div className={classNameDigitContainer}>
      {type === D_DAY_TYPE.DAY && timeString[0] === '0' && padReplaceD ? (
        <div className={classNameDigit}>D-</div>
      ) : (
        <div className={classNameDigit}>{timeString[0]}</div>
      )}
      <div className={classNameDigit}>{timeString[1]}</div>
    </div>
  )
}

interface DdayCounterType {
  initialDay: Date
  targetDay: Date // UTC 기준으로 넣어주어야 정상 동작
  types: D_DAY_TYPE[] // ex) [D_DAY_TYPE.HOUR, D_DAY_TYPE.MIN, D_DAY_TYPE.SEC]
  padReplaceD?: boolean
  classNameTotalContainer?: string
  classNameDigitContainer?: string
  classNameDigitWrapper?: string
  classNameDigit?: string
  seperator?: React.ReactNode
  text?: React.ReactNode
  header?: React.ReactNode
  bottom?: React.ReactNode
  typeLabel?: React.ReactNode[] // D_DAY_TYPE과 길이가 일치해야 함
  classNameTypeLabel?: string
}
export const DdayCounter = (props: DdayCounterType) => {
  /**
   * PartialDdayCounter 여러 개 모은 컴포넌트
   * seperator로 날짜/시간/분/초 구분할 컴포넌트 넣어주면 반영
   */
  const {
    initialDay,
    targetDay,
    types,
    classNameTotalContainer,
    classNameDigitWrapper,
    classNameDigitContainer,
    classNameDigit,
    seperator,
    text,
    typeLabel,
    classNameTypeLabel,
    header,
    bottom,
    padReplaceD,
  } = props
  return (
    <div className="text-center">
      {types.length > 1 && header}
      <div className={classNameTotalContainer}>
        {types.map((type, i) => (
          <div key={`PartialDdayCounter_${type}`} className={classNames('flex', classNameDigitWrapper)}>
            {i > 0 && seperator}
            <div>
              {types.length == 1 && header}
              <PartialDdayCounter
                initialDay={initialDay}
                targetDay={targetDay}
                type={type}
                classNameDigitContainer={classNameDigitContainer}
                classNameDigit={classNameDigit}
                padReplaceD={padReplaceD}
              />
              {typeLabel && typeLabel[i] && <div className={classNameTypeLabel}>{typeLabel[i]}</div>}
            </div>
            {types.length == 1 && text}
          </div>
        ))}
        {types.length > 1 && text}
        {bottom}
      </div>
    </div>
  )
}
